import type { TenantRoute, TenantRoutePoint } from '../types/TenantRouteType';

export const MAX_TENANT_ROUTES = 10;
export const MIN_CIRCLE_RADIUS_M = 1000;
export const MAX_CIRCLE_RADIUS_M = 1000000;

export const ROUTE_COLORS = [
    '#006D77',
    '#E29578',
    '#3D5A80',
    '#EE6C4D',
    '#2A9D8F',
    '#E9C46A',
    '#264653',
    '#F4A261',
    '#457B9D',
    '#E76F51'
];

const SWEDEN_CENTER = { lat: 62, lng: 15 };

export function routeColor(index: number) {
    return ROUTE_COLORS[index % ROUTE_COLORS.length];
}

// Copied from packages/webapp/src/utils/tenantRoutes.ts
const EARTH_RADIUS_M = 6371200;
export const HOME_ROUTE_RADIUS_M = 100000;
const round6 = (x: number) => Math.round(x * 1e6) / 1e6;

/** Polygon approximating a circle (same math as circleToPolygonPoints in the API). */
export function circleRoutePoints(
    lat: number,
    lng: number,
    radiusM: number,
    n = 24
): TenantRoutePoint[] {
    const lat1 = (lat * Math.PI) / 180;
    const lng1 = (lng * Math.PI) / 180;
    const d = radiusM / EARTH_RADIUS_M;
    return Array.from({ length: n }, (_, i) => {
        const bearing = (2 * Math.PI * i) / n;
        const lat2 = Math.asin(
            Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing)
        );
        const lng2 = lng1 + Math.atan2(
            Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
            Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
        );
        return {
            lat: round6((lat2 * 180) / Math.PI),
            lng: round6(((((lng2 * 180) / Math.PI) + 540) % 360) - 180)
        };
    });
}

/** Square polygon (4 corners) around a point; `halfWidthM` is the distance from the centre to each side. */
export function squareRoutePoints(
    lat?: number | null,
    lng?: number | null,
    halfWidthM: number = HOME_ROUTE_RADIUS_M
): TenantRoutePoint[] {
    const centerLat = lat || SWEDEN_CENTER.lat;
    const centerLng = lng || SWEDEN_CENTER.lng;
    const latOff = (halfWidthM / EARTH_RADIUS_M) * (180 / Math.PI);
    const lngOff = latOff / Math.max(Math.cos((centerLat * Math.PI) / 180), 0.2);

    return [
        { lat: round6(centerLat + latOff), lng: round6(centerLng - lngOff) },
        { lat: round6(centerLat + latOff), lng: round6(centerLng + lngOff) },
        { lat: round6(centerLat - latOff), lng: round6(centerLng + lngOff) },
        { lat: round6(centerLat - latOff), lng: round6(centerLng - lngOff) }
    ];
}

/** Polygon -> circle: the polygon's centroid (mean of the corners) and a radius that covers it. */
export function polygonToCircle(points: TenantRoutePoint[]) {
    const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
    const lng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
    const maxDistanceM = Math.max(...points.map((p) => {
        const dLat = ((p.lat - lat) * Math.PI) / 180;
        const dLng = ((p.lng - lng) * Math.PI) / 180;
        const a = Math.sin(dLat / 2) ** 2
            + Math.cos((lat * Math.PI) / 180) * Math.cos((p.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
        return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a));
    }));
    return {
        center: { lat, lng },
        radiusM: Math.min(MAX_CIRCLE_RADIUS_M, Math.max(MIN_CIRCLE_RADIUS_M, Math.ceil(maxDistanceM / 1000) * 1000))
    };
}

export function nextRouteName(routes: TenantRoute[]) {
    return `Rutt ${routes.length + 1}`;
}
